import dayjs from "dayjs";
import { LeaveApprovals } from "components/leaveApprovals/types";
import { LeaveRequestsFilters } from "./types";

type LeaveRequestSearchParams = NonNullable<
  LeaveRequestsFilters["searchParams"]
>;

interface NormalizedDateRange {
  startDate?: string;
  endDate?: string;
}

export function splitDateRangeFilters(
  searchParams?: LeaveRequestSearchParams,
): {
  hasDateRange: boolean;
  dateRange: NormalizedDateRange;
  searchParams: Omit<LeaveRequestSearchParams, "startDate" | "endDate">;
} {
  const { startDate, endDate, ...rest } = searchParams || {};

  return {
    hasDateRange: Boolean(startDate || endDate),
    dateRange: normalizeDateRange({ startDate, endDate }),
    searchParams: rest,
  };
}

export function filterLeaveRequestsByDateRange(
  data: LeaveApprovals[],
  searchParams?: LeaveRequestSearchParams,
) {
  const { hasDateRange, dateRange } = splitDateRangeFilters(searchParams);

  if (!hasDateRange) {
    return data;
  }

  return data.filter((item) => matchesDateRange(item, dateRange));
}

function normalizeDateRange({
  startDate,
  endDate,
}: NormalizedDateRange): NormalizedDateRange {
  if (!startDate || !endDate) {
    return { startDate, endDate };
  }

  const minDate = dayjs(startDate);
  const maxDate = dayjs(endDate);

  if (minDate.isAfter(maxDate, "day")) {
    return {
      startDate: endDate,
      endDate: startDate,
    };
  }

  return { startDate, endDate };
}

function matchesDateRange(
  item: LeaveApprovals,
  { startDate, endDate }: NormalizedDateRange,
) {
  const requestStartDate = dayjs(item.startDate);
  const requestEndDate = item.endDate ? dayjs(item.endDate) : requestStartDate;

  if (!requestStartDate.isValid()) {
    return false;
  }

  const normalizedRequestEndDate = requestEndDate.isValid()
    ? requestEndDate
    : requestStartDate;

  if (startDate && endDate) {
    const filterStartDate = dayjs(startDate);
    const filterEndDate = dayjs(endDate);

    return (
      !normalizedRequestEndDate.isBefore(filterStartDate, "day") &&
      !requestStartDate.isAfter(filterEndDate, "day")
    );
  }

  if (startDate) {
    const filterStartDate = dayjs(startDate);
    return !normalizedRequestEndDate.isBefore(filterStartDate, "day");
  }

  if (endDate) {
    const filterEndDate = dayjs(endDate);
    return !requestStartDate.isAfter(filterEndDate, "day");
  }

  return true;
}
